/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path tplutils
 * @description Teplate utils
 */
/* eslint-disable no-plusplus, no-continue */
import i18n from "i18next";
import * as React from "react";
let i = 1;
export const uuid = () => {
  i += 1;
  return `comp-${Date.now()}-${i}`;
};
export const uuid2 = () => {
  i += 1;
  return `comp-${Date.now()}-${i}`;
};
export const templateReplace = (str: string, values: any) =>
  str.replace(/\${(.*?)}/g, (_, g: any) => {
    return values[g] || "";
  });
export const pick = (obj: any, path: string, defaultValue: any = undefined) => {
  const paths = path.split(".");
  let current = obj;
  for (let i = 0; i < paths.length; i += 1) {
    if (typeof current !== "object") {
      return defaultValue;
    }
    current = current[paths[i]];
  }
  return typeof current === "undefined" ? defaultValue : current;
};
export const stringTemplate = (
  str: string,
  values: {
    [key: string]: any;
  } = {}
) => {
  if (!str) return "";
  let value = str;
  Object.keys(values).forEach((key) => {
    const r = new RegExp(`\\\${${key}\\.(.*?)}`, "g");
    const obj = values[key];
    value = value.replace(r, (_, objectKey: string) => {
      if (!obj) {
        return "";
      }
      const fi = objectKey.indexOf(".toFixed(");
      if (fi > 0) {
        const keyName = objectKey.substr(0, fi);
        const [, value] = objectKey.split("toFixed(");
        const v = parseInt(value, 10);
        const n = Number(obj[keyName]);
        if (!Number.isNaN(v) && !Number.isNaN(n)) {
          return n.toFixed(v);
        }
      }
      //       pick
      if (objectKey.indexOf("${") >= 0) {
        return pick(obj, stringTemplate(objectKey, values), "");
      }
      return pick(obj, objectKey, "");
    });
  });
  // eslint-disable-next-line
  const trans = new RegExp(`t\\\((.*?)\\\)`, "g");
  value = value.replace(trans, (_, objectKey: string, index) => {
    if (
      typeof value[index - 1] !== "undefined" &&
      value[index - 1].match(/\w/)
    ) {
      return _;
    }
    return i18n.t(objectKey);
  });
  return value;
};
export type CodeRow = string | Array<CodeRow>;
/**
 *
 * Function to transform text blocks to list of arrays, which can then be transformed to
 * formatted text blocks to be used in editors.
 *
 * @param inputTxt
 * @returns
 */
export function TextToArray(inputTxt: CodeRow): CodeRow {
  if (Array.isArray(inputTxt)) return inputTxt;
  const lines = inputTxt.split("\n");
  let outputIndex = 0;
  let currentTabIndex = 0;
  const calculateTabIndex = (line: string = lines[outputIndex]) => {
    if (line.trim().length === 0) {
      return currentTabIndex;
    }
    let cnt = 0;
    for (let i = 0; i < line.length; i++) {
      if (line[i].match(/\t/)) {
        cnt += 2;
        continue;
      }
      if (line[i].match(/\s/)) {
        cnt++;
      } else {
        break;
      }
    }
    return Math.floor(cnt / 2);
  };
  const consumeLines = () => {
    const initialTabIndex = calculateTabIndex();
    currentTabIndex = initialTabIndex;
    const result: CodeRow = [];
    while (outputIndex < lines.length && initialTabIndex <= currentTabIndex) {
      const lineTxt = lines[outputIndex].trim();
      if (lineTxt.trim().length === 0) {
        result.push("");
      } else {
        result.push(lineTxt);
      }
      outputIndex++;
      if (outputIndex >= lines.length) {
        break;
      }
      currentTabIndex = calculateTabIndex();
      if (currentTabIndex > initialTabIndex) {
        result.push(consumeLines());
        if (currentTabIndex < initialTabIndex) {
          break;
        }
      }
    }
    return result;
  };
  return consumeLines();
}
export function ArrayToText(inputArray: CodeRow) {
  let parts: string[] = [];
  const walk = (value: CodeRow) => {
    if (typeof value === "string") {
      parts.push(value);
    } else {
      value.forEach((v) => {
        walk(v);
      });
    }
  };
  walk(inputArray);
  return parts.join("\n");
}
export function useArrayToText(inputArray: CodeRow) {
  const value = React.useMemo(() => {
    let parts: string[] = [];
    const walk = (value: CodeRow) => {
      if (typeof value === "string") {
        parts.push(value);
      } else {
        value.forEach((v) => {
          walk(v);
        });
      }
    };
    walk(inputArray);
    return parts.join("\n");
  }, [inputArray]);
  return value;
}
//  format prettyvalue of date received from MEA (in ISO format yyyy-mm-dd)
//  to more presentable format dd/mm/yyyy
export const formatDatePrettyValue = (date: string = ""): string => {
  const parts = date.trim().split("-");
  if (parts.length !== 3) return date;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};
//  cut long text
export const cutText = (
  text: string = "",
  minLength: number,
  maxLength?: number
): string => {
  maxLength = maxLength || minLength;
  if (text.length <= maxLength) return text;
  let pos = text.indexOf(" ", minLength);
  if (pos < minLength) return `${text.slice(0, maxLength)}...`;
  return `${text.slice(0, pos)}...`;
};
// tint RGB color in mode #AABBCC - do lighter color
// tint_factor is between 0...1
// from https://stackoverflow.com/questions/6615002/given-an-rgb-value-how-do-i-create-a-tint-or-shade/6615053
export const tintRGB = (hex: string, tint_factor: number): string => {
  // strip the leading # if it's there
  hex = hex.replace(/^\s*#|\s*$/g, "");
  const curR = parseInt(hex.substring(1, 3), 16);
  const curG = parseInt(hex.substring(3, 5), 16);
  const curB = parseInt(hex.substring(5, 7), 16);
  const newR = curR + (255 - curR) * tint_factor;
  const newG = curG + (255 - curG) * tint_factor;
  const newB = curB + (255 - curB) * tint_factor;
  const result = `#${newR.toString(16).substring(0, 2)}${newG
    .toString(16)
    .substring(0, 2)}${newB.toString(16).substring(0, 2)}`.toUpperCase();
  return result;
};
// shade RGB color in mode #AABBCC - do darker color
// shade_factor is between 0...1
// from https://stackoverflow.com/questions/6615002/given-an-rgb-value-how-do-i-create-a-tint-or-shade/6615053
export const shadeRGB = (hex: string, shade_factor: number): string => {
  // strip the leading # if it's there
  hex = hex.replace(/^\s*#|\s*$/g, "");
  const curR = parseInt(hex.substring(1, 3), 16);
  const curG = parseInt(hex.substring(3, 5), 16);
  const curB = parseInt(hex.substring(5, 7), 16);
  const newR = curR * (1 - shade_factor);
  const newG = curG * (1 - shade_factor);
  const newB = curB * (1 - shade_factor);
  const result = `#${newR.toString(16).substring(0, 2)}${newG
    .toString(16)
    .substring(0, 2)}${newB.toString(16).substring(0, 2)}`.toUpperCase();
  return result;
};

export const mea_t = (
  attribute: string,
  JS_ATTR: string | undefined,
  lang: string
): string => {
  try {
    if (!JS_ATTR) return "missing metadata";

    const parsed = JSON.parse(JS_ATTR);
    return (
      parsed?.["Localized-Text-Attributes"]?.[lang]?.[`${attribute}`] || ``
    );
  } catch (e) {
    console.error(`meaTranlation failed, attribute: ${attribute}`, e);
    console.error(JS_ATTR);
    return "undefined";
  }
};

export const getElementName = (element: any, language: string) => {
  if (!element) {
    return "";
  }

  const translatedName = element["JS_ATTR"]
    ? mea_t("Name", element["JS_ATTR"], language) || element.name
    : element.name;
  return translatedName;
};

export const getTranslatedAttribute = (
  element: any,
  attribute: string,
  language: string
) => {
  if (!element) {
    return "";
  }
  const attr = attribute.toLocaleLowerCase();
  const translatedName = element["JS_ATTR"]
    ? mea_t(attribute, element["JS_ATTR"], language) || element[attr]
    : element[attr];
  if (translatedName === "undefined" && element[attr]) return element[attr];

  return translatedName;
};
/**
 *
 * makes sure that a hex color is changed to the format #rrggbb
 *
 * @param {string} colorHex hex input either as #rrggbb or #rgb
 * @param {boolean} prefixWithHash toggle whether to output the result as #rrggbb or rrggbb
 * @returns {string} hex string in format #rrggbb
 */
export const get6DigitsHex = (
  colorHex: string,
  prefixWithHash: boolean = true
): string => {
  let hex: string;
  if (colorHex.length === 3)
    hex = `${colorHex[0]}${colorHex[0]}${colorHex[1]}${colorHex[1]}${colorHex[2]}${colorHex[2]}`;
  else hex = colorHex;
  return prefixWithHash ? `#${hex}` : hex;
};
export function getKPITypeFromParentType(parentType: string) {
  const kpiType = (() => {
    switch (parentType) {
      case "Government Entity":
        return "Plan KPI";
      case "Aim":
      case "Priority":
        return "Priority KPI";
      case "Theme":
        return "Theme KPI";
      default:
        return parentType;
    }
  })();
  return i18n.t(kpiType);
}
export function ordinalSuffixOf(i: number) {
  let j = i % 10;
  let k = i % 100;
  if (j === 1 && k !== 11) {
    return `${i}st`;
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }
  return `${i}th`;
}
/// source: https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
export const arrayMove = (arr: any[], fromIndex: number, toIndex: number) => {
  const newArr = [...arr];
  newArr.splice(toIndex, 0, newArr.splice(fromIndex, 1)[0]);
  return newArr;
};
/// source: https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
export const base64toBlob = (base64: string) => {
  // Split into two parts
  const parts = base64.split(";base64,");
  // Hold the content type
  const imageType = parts[0].split(":")[1];
  // Decode Base64 string
  const decodedData = window.atob(parts[1]);
  // Create UNIT8ARRAY of size same as row data length
  const uInt8Array = new Uint8Array(decodedData.length);
  // Insert all character code into uInt8Array
  for (let i = 0; i < decodedData.length; ++i) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }
  // Return BLOB image after conversion
  return new Blob([uInt8Array], { type: imageType });
};
/**
 * calculate size in bytes
 * source: https://lioncoding.com/calculate-a-file-size-from-base64-string/
 */
export const getImgSize = (base64: string): number => {
  const start = `data:image/png;base64,`;
  const base64ImageData = base64.slice(start.length);
  const base64Length = base64ImageData.length;
  let fileSizeInByte = Math.ceil(base64Length / 4) * 3;
  if (base64Length >= 2) {
    let paddings = base64ImageData.slice(-2);
    if (paddings === "==") fileSizeInByte -= 2;
    else if (paddings[1] === "=") fileSizeInByte -= 1;
  }
  return fileSizeInByte;
};
export const getImgDimensions = async (base64: string) => {
  const img = new Image();
  img.src = base64;
  await img.decode();
  return { width: img.width, height: img.height };
};
/**
 * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
 * images to fit into a certain area.
 * source: https://stackoverflow.com/a/14731922
 * @param {Number} srcWidth width of source image
 * @param {Number} srcHeight height of source image
 * @param {Number} maxWidth maximum available width
 * @param {Number} maxHeight maximum available height
 * @return {Object} { width, height }
 */
export const calculateAspectRatioFit = (
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  let ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: srcWidth * ratio, height: srcHeight * ratio };
};
/// source: https://stackoverflow.com/a/57272491/5935917
export const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
/* eslint-disable */
/*********************************************************************
 * @function      : ordinalsAr(number [, isFeminine])
 * @purpose       : Converts numbers from 1 to 99 to Arabic Ordinal String
 * @version       : 1.00
 * @author        : Mohsen Alyafei
 * @date          : 27 Jan 2022
 * @Licence       : MIT
 * @param         : {number} Integer from 1 to 99
 * @param         : [isFeminine] the subject's gender:
 *                  false (default) --> Masculine subject (e.g. output الأول الثاني الثالث)
 *                  true            --> Feminine subject  (e.g. output الأولى الثانية الثالثة)
 * @returns       : {string} The ordinal Arabic Text.
 * source: https://stackoverflow.com/questions/70846777/how-to-convert-numbers-from-1-to-99-to-the-equivalent-arabic-ordinal-number-st
 **********************************************************************/
export function ordinalsAr(num: number, isFeminine = false) {
  num %= 100; // only handle the lowest 2 digits (1-99) ignore others
  let the = "ال", // set this to "" if you don't want the output prefixed with letters "ال"
    unit = num % 10,
    ordinal =
      the +
      [
        ,
        "أول",
        "ثاني",
        "ثالث",
        "رابع",
        "خامس",
        "سادس",
        "سابع",
        "ثامن",
        "تاسع",
        "عاشر",
      ][num === 10 ? num : unit], // letters for lower part of ordinal string
    female = isFeminine ? "ة" : "", // add letter "ة" if Feminine
    ones = (unit === 1 ? the + "حادي" : ordinal) + female; // special cases for 21, 31, 41, etc.
  return num < 11
    ? ordinal + (isFeminine && num === 1 ? "ى" : female) // from 1 to 10
    : num < 20
    ? ones + " عشر" + female // from 11 to 19
    : (unit ? ones + " و" : "") + // else 20 to 99
      "ال" + // always add "ال"
      [, , "عشر", "ثلاث", "أربع", "خمس", "ست", "سبع", "ثمان", "تسع"][
        ~~(num / 10)
      ] + // letters for 20, 30, 40...
      "ون";
}
/* eslint-enable */
